import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  getCachedTimerOneMinBlobUrl,
  primeTimerOneMinSoundCache,
  unlockSlideTimerOneMinuteChimeAudio,
} from '../../utils/timerOneMinuteSoundCache';

export type EduCoordiTimerModalProps = {
  open: boolean;
  onClose: () => void;
  /** 타이머 실행 중 여부 콜백 (네비 is-on 표시용) */
  onRunningChange?: (running: boolean) => void;
};

const TIMER_1MIN_FALLBACK = '/sounds/TIMER_1MIN.mp3';

const TIMER_MIN_W = 300;
const TIMER_MIN_H = 350;
const TIMER_MINI_W = 200;
const TIMER_MINI_H = 80;

function getViewportSize() {
  if (typeof window === 'undefined') return { w: 1920, h: 1080 };
  return { w: window.innerWidth, h: window.innerHeight };
}

/** 모달 사각형이 브라우저 뷰포트 밖으로 나가지 않도록 크기·위치 보정 */
function clampModalInViewport(
  x: number,
  y: number,
  w: number,
  h: number,
  viewport?: { w: number; h: number }
) {
  const { w: vw, h: vh } = viewport ?? getViewportSize();
  const width = Math.min(Math.max(w, 0), vw);
  const height = Math.min(Math.max(h, 0), vh);
  const maxX = Math.max(0, vw - width);
  const maxY = Math.max(0, vh - height);
  return {
    size: { width, height },
    position: {
      x: Math.min(Math.max(0, x), maxX),
      y: Math.min(Math.max(0, y), maxY),
    },
  };
}

function getDefaultLayout() {
  const viewport = getViewportSize();
  const width = Math.min(720, viewport.w * 0.88);
  const height = Math.min(820, viewport.h * 0.88);
  const clamped = clampModalInViewport(
    (viewport.w - width) / 2,
    (viewport.h - height) / 2,
    width,
    height,
    viewport
  );
  return { ...clamped, viewport };
}

async function resolveTimerSoundSrc(): Promise<string> {
  const blobUrl = await getCachedTimerOneMinBlobUrl();
  if (blobUrl) return blobUrl;
  return TIMER_1MIN_FALLBACK;
}

function playOneMinuteChime(
  volume: number,
  isMuted: boolean,
  audioRef: React.MutableRefObject<HTMLAudioElement | null>
): void {
  void (async () => {
    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      const src = await resolveTimerSoundSrc();
      const audio = new Audio(src);
      audioRef.current = audio;
      audio.volume = isMuted ? 0 : volume;
      await audio.play();
    } catch (error) {
      console.error('타이머 1분 알람 음원 재생 실패:', error);
    }
  })();
}

const EduCoordiTimerModal: React.FC<EduCoordiTimerModalProps> = ({
  open,
  onClose,
  onRunningChange,
}) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [totalSeconds, setTotalSeconds] = useState(0);
  const [isStopwatch, setIsStopwatch] = useState(false);
  const [editingPart, setEditingPart] = useState<'minutes' | 'seconds' | null>(null);
  const [editingMinutes, setEditingMinutes] = useState('00');
  const [editingSeconds, setEditingSeconds] = useState('00');
  const [volume, setVolume] = useState(1.0);
  const [isMuted, setIsMuted] = useState(false);

  const [position, setPosition] = useState(() => getDefaultLayout().position);
  const [size, setSize] = useState(() => getDefaultLayout().size);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStart, setResizeStart] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    left: 0,
    top: 0,
  });
  const [resizeDirection, setResizeDirection] = useState<'nw' | 'ne' | 'sw' | 'se' | null>(null);
  const [windowSize, setWindowSize] = useState(() => {
    if (typeof window === 'undefined') return { w: 1920, h: 1080 };
    return { w: window.innerWidth, h: window.innerHeight };
  });
  const [isDraggingVolume, setIsDraggingVolume] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const timerIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const hasPlayedOneMinuteSound = useRef(false);
  const audioStopTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const volumeRef = useRef(volume);
  const isMutedRef = useRef(isMuted);
  const positionRef = useRef(position);
  const sizeRef = useRef(size);
  const isMinimizedRef = useRef(isMinimized);
  volumeRef.current = volume;
  isMutedRef.current = isMuted;
  positionRef.current = position;
  sizeRef.current = size;
  isMinimizedRef.current = isMinimized;

  useEffect(() => {
    if (!open) return;
    setIsMinimized(false);
    setIsMaximized(false);
    const layout = getDefaultLayout();
    setSize(layout.size);
    setPosition(layout.position);
    setWindowSize(layout.viewport);
    setIsDragging(false);
    setIsResizing(false);
    setResizeDirection(null);
    primeTimerOneMinSoundCache();
  }, [open]);

  useEffect(() => {
    if (!open || !isMaximized) return;
    const syncViewport = () => {
      setWindowSize({ w: window.innerWidth, h: window.innerHeight });
    };
    syncViewport();
    window.addEventListener('resize', syncViewport);
    return () => window.removeEventListener('resize', syncViewport);
  }, [open, isMaximized]);

  useEffect(() => {
    if (!open || isMaximized) return;
    const onResize = () => {
      const viewport = getViewportSize();
      setWindowSize(viewport);
      const mini = isMinimizedRef.current;
      const curSize = sizeRef.current;
      const curPos = positionRef.current;
      const w = mini ? TIMER_MINI_W : curSize.width;
      const h = mini ? TIMER_MINI_H : curSize.height;
      const clamped = clampModalInViewport(curPos.x, curPos.y, w, h, viewport);
      if (!mini) setSize(clamped.size);
      setPosition(clamped.position);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open, isMaximized]);

  const modalWidth = isMaximized ? windowSize.w : size.width;
  const modalHeight = isMaximized ? windowSize.h : size.height;
  const modalLeft = isMaximized ? 0 : position.x;
  const modalTop = isMaximized ? 0 : position.y;

  useEffect(() => {
    if (!open) {
      setIsRunning(false);
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      if (audioStopTimeoutRef.current) {
        clearTimeout(audioStopTimeoutRef.current);
        audioStopTimeoutRef.current = null;
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }
      hasPlayedOneMinuteSound.current = false;
      setIsStopwatch(false);
    }
  }, [open]);

  useEffect(() => {
    onRunningChange?.(isRunning);
  }, [isRunning, onRunningChange]);

  useEffect(() => {
    if (!open) return;

    if (isRunning) {
      timerIntervalRef.current = setInterval(() => {
        if (isStopwatch) {
          setRemainingSeconds((prev) => prev + 1);
        } else {
          setRemainingSeconds((prev) => {
            const newValue = prev - 1;

            if (newValue <= 60 && !hasPlayedOneMinuteSound.current) {
              hasPlayedOneMinuteSound.current = true;
              playOneMinuteChime(volumeRef.current, isMutedRef.current, audioRef);
            } else if (audioRef.current && !audioRef.current.paused) {
              audioRef.current.volume = isMutedRef.current ? 0 : volumeRef.current;
            }

            if (newValue <= 0) {
              setIsRunning(false);
              if (audioRef.current) {
                if (audioStopTimeoutRef.current) {
                  clearTimeout(audioStopTimeoutRef.current);
                }
                audioStopTimeoutRef.current = setTimeout(() => {
                  if (audioRef.current) {
                    audioRef.current.pause();
                    audioRef.current.currentTime = 0;
                    audioRef.current = null;
                  }
                  audioStopTimeoutRef.current = null;
                }, 2000);
              }
              if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
                timerIntervalRef.current = null;
              }
              hasPlayedOneMinuteSound.current = false;
              return 0;
            }
            return newValue;
          });
        }
      }, 1000);
    } else if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [open, isRunning, isStopwatch]);

  useEffect(() => {
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (isMaximized) return;
      const target = e.target as HTMLElement;
      if (
        target.closest('button') ||
        target.closest('input') ||
        target.closest('[data-volume-slider]') ||
        target.closest('[data-volume-handle]') ||
        target.tagName === 'BUTTON' ||
        target.tagName === 'INPUT'
      ) {
        return;
      }
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    },
    [position, isMaximized]
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (isMaximized) return;
      const target = e.target as HTMLElement;
      if (
        target.closest('button') ||
        target.closest('input') ||
        target.closest('[data-volume-slider]') ||
        target.closest('[data-volume-handle]') ||
        target.tagName === 'BUTTON' ||
        target.tagName === 'INPUT'
      ) {
        return;
      }
      const touch = e.touches[0];
      if (!touch) return;
      setIsDragging(true);
      setDragStart({
        x: touch.clientX - position.x,
        y: touch.clientY - position.y,
      });
      e.preventDefault();
    },
    [position, isMaximized]
  );

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const mini = isMinimizedRef.current;
      const w = mini ? TIMER_MINI_W : sizeRef.current.width;
      const h = mini ? TIMER_MINI_H : sizeRef.current.height;
      const next = clampModalInViewport(e.clientX - dragStart.x, e.clientY - dragStart.y, w, h);
      setPosition(next.position);
    };
    const handleMouseUp = () => setIsDragging(false);
    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (!touch) return;
      const mini = isMinimizedRef.current;
      const w = mini ? TIMER_MINI_W : sizeRef.current.width;
      const h = mini ? TIMER_MINI_H : sizeRef.current.height;
      const next = clampModalInViewport(touch.clientX - dragStart.x, touch.clientY - dragStart.y, w, h);
      setPosition(next.position);
      e.preventDefault();
    };
    const handleTouchEnd = () => setIsDragging(false);

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, dragStart]);

  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent, direction: 'nw' | 'ne' | 'sw' | 'se') => {
      if (isMinimized || isMaximized) return;
      e.stopPropagation();
      setResizeDirection(direction);
      setIsResizing(true);
      setResizeStart({
        x: e.clientX,
        y: e.clientY,
        width: size.width,
        height: size.height,
        left: position.x,
        top: position.y,
      });
    },
    [size, position, isMinimized, isMaximized]
  );

  useEffect(() => {
    if (!isResizing || !resizeDirection) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - resizeStart.x;
      const deltaY = e.clientY - resizeStart.y;
      let newWidth = resizeStart.width;
      let newHeight = resizeStart.height;
      let newX = resizeStart.left;
      let newY = resizeStart.top;

      switch (resizeDirection) {
        case 'nw':
          newWidth = Math.max(TIMER_MIN_W, resizeStart.width - deltaX);
          newHeight = Math.max(TIMER_MIN_H, resizeStart.height - deltaY);
          newX = resizeStart.left + (resizeStart.width - newWidth);
          newY = resizeStart.top + (resizeStart.height - newHeight);
          break;
        case 'ne':
          newWidth = Math.max(TIMER_MIN_W, resizeStart.width + deltaX);
          newHeight = Math.max(TIMER_MIN_H, resizeStart.height - deltaY);
          newY = resizeStart.top + (resizeStart.height - newHeight);
          break;
        case 'sw':
          newWidth = Math.max(TIMER_MIN_W, resizeStart.width - deltaX);
          newHeight = Math.max(TIMER_MIN_H, resizeStart.height + deltaY);
          newX = resizeStart.left + (resizeStart.width - newWidth);
          break;
        case 'se':
          newWidth = Math.max(TIMER_MIN_W, resizeStart.width + deltaX);
          newHeight = Math.max(TIMER_MIN_H, resizeStart.height + deltaY);
          break;
      }

      const clamped = clampModalInViewport(newX, newY, newWidth, newHeight);
      setSize(clamped.size);
      setPosition(clamped.position);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      setResizeDirection(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, resizeDirection, resizeStart]);

  const formatTime = useCallback((totalSecs: number) => {
    const mins = Math.floor(totalSecs / 60);
    const secs = totalSecs % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  const handleMinutesClick = useCallback(() => {
    if (isRunning) return;
    setEditingPart('minutes');
    setEditingMinutes('');
  }, [isRunning]);

  const handleSecondsClick = useCallback(() => {
    if (isRunning) return;
    setEditingPart('seconds');
    setEditingSeconds('');
  }, [isRunning]);

  const handleMinutesBlur = useCallback(() => {
    setEditingPart(null);
    const mins = Math.max(0, Math.min(59, parseInt(editingMinutes, 10) || 0));
    setMinutes(mins);
    const total = mins * 60 + seconds;
    setTotalSeconds(total);
    if (!isRunning) setRemainingSeconds(total);
    setEditingMinutes('');
  }, [editingMinutes, seconds, isRunning]);

  const handleSecondsBlur = useCallback(() => {
    setEditingPart(null);
    const secs = Math.max(0, Math.min(59, parseInt(editingSeconds, 10) || 0));
    setSeconds(secs);
    const total = minutes * 60 + secs;
    setTotalSeconds(total);
    if (!isRunning) setRemainingSeconds(total);
    setEditingSeconds('');
  }, [editingSeconds, minutes, isRunning]);

  const handleMinutesKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') handleMinutesBlur();
      else if (e.key === 'Escape') setEditingPart(null);
    },
    [handleMinutesBlur]
  );

  const handleSecondsKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') handleSecondsBlur();
      else if (e.key === 'Escape') setEditingPart(null);
    },
    [handleSecondsBlur]
  );

  const progress = useMemo(() => {
    if (isStopwatch) return (remainingSeconds % 60) / 60;
    if (totalSeconds === 0) return 0;
    return remainingSeconds / totalSeconds;
  }, [remainingSeconds, totalSeconds, isStopwatch]);

  const completedMinutes = useMemo(() => {
    if (!isStopwatch) return 0;
    return Math.floor(remainingSeconds / 60);
  }, [remainingSeconds, isStopwatch]);

  const isCurrentMinuteEven = useMemo(() => {
    if (!isStopwatch) return true;
    return Math.floor(remainingSeconds / 60) % 2 === 0;
  }, [remainingSeconds, isStopwatch]);

  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof document !== 'undefined') {
      return document.documentElement.getAttribute('data-theme') !== 'light';
    }
    return false;
  });

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
          setIsDarkMode(document.documentElement.getAttribute('data-theme') !== 'light');
        }
      });
    });
    if (typeof document !== 'undefined') {
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['data-theme'],
      });
    }
    return () => observer.disconnect();
  }, []);

  const getStopwatchColor = useCallback(
    (isEven: boolean) => {
      if (isEven) return 'white';
      return isDarkMode ? '#5B5B5B' : '#5F8581';
    },
    [isDarkMode]
  );

  const isUnderOneMinute = useMemo(
    () => remainingSeconds <= 60 && remainingSeconds > 0,
    [remainingSeconds]
  );

  const handleStart = useCallback(() => {
    void unlockSlideTimerOneMinuteChimeAudio();

    if (remainingSeconds > 0) {
      // 정지 상태에서 시작: 현재 설정된 시간을 처음으로 리셋하여 시작
      setTotalSeconds(remainingSeconds);
      setIsStopwatch(false);
      setIsRunning(true);
      setEditingPart(null);
      hasPlayedOneMinuteSound.current = false;

      if (remainingSeconds <= 60) {
        hasPlayedOneMinuteSound.current = true;
        playOneMinuteChime(volume, isMuted, audioRef);
      }
    } else {
      const editingMin = parseInt(editingMinutes, 10) || 0;
      const editingSec = parseInt(editingSeconds, 10) || 0;
      const editingTotal = editingMin * 60 + editingSec;

      if (editingTotal <= 0 || (editingMin === 0 && editingSec === 0)) {
        setIsStopwatch(true);
        setRemainingSeconds(0);
        setTotalSeconds(0);
        setIsRunning(true);
        setEditingPart(null);
        hasPlayedOneMinuteSound.current = false;
        return;
      }

      setIsStopwatch(false);
      setTotalSeconds(editingTotal);
      setRemainingSeconds(editingTotal);
      setMinutes(editingMin);
      setSeconds(editingSec);
      setIsRunning(true);
      setEditingPart(null);
      hasPlayedOneMinuteSound.current = false;

      if (editingTotal <= 60 && editingTotal > 0) {
        hasPlayedOneMinuteSound.current = true;
        playOneMinuteChime(volume, isMuted, audioRef);
      }
    }
  }, [remainingSeconds, isMuted, volume, editingMinutes, editingSeconds]);

  const handlePause = useCallback(() => {
    setIsRunning(false);
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
    }
    if (audioStopTimeoutRef.current) {
      clearTimeout(audioStopTimeoutRef.current);
      audioStopTimeoutRef.current = null;
    }
  }, []);

  const handleReset = useCallback(() => {
    setIsRunning(false);
    setIsStopwatch(false);
    setRemainingSeconds(0);
    setTotalSeconds(0);
    setMinutes(0);
    setSeconds(0);
    setEditingPart(null);
    setEditingMinutes('00');
    setEditingSeconds('00');

    if (audioStopTimeoutRef.current) {
      clearTimeout(audioStopTimeoutRef.current);
      audioStopTimeoutRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    hasPlayedOneMinuteSound.current = false;
  }, []);

  const handleVolumeChange = useCallback((newVolume: number) => {
    const clampedVolume = Math.max(0, Math.min(1, newVolume));
    setVolume(clampedVolume);
    setIsMuted(false);
    if (audioRef.current) {
      audioRef.current.volume = clampedVolume;
    }
  }, []);

  const handleMuteToggle = useCallback(() => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    if (audioRef.current) {
      audioRef.current.volume = newMuted ? 0 : volume;
    }
  }, [isMuted, volume]);

  const handleVolumeSliderClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      handleVolumeChange(Math.max(0, Math.min(1, x / rect.width)));
    },
    [handleVolumeChange]
  );

  const handleVolumeSliderMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      setIsDraggingVolume(true);
      handleVolumeSliderClick(e);
    },
    [handleVolumeSliderClick]
  );

  useEffect(() => {
    if (!isDraggingVolume) return;
    const handleMouseMove = (e: MouseEvent) => {
      const slider = document.querySelector(
        '.cp-edu-timer-overlay [data-volume-slider]'
      ) as HTMLElement | null;
      if (!slider) return;
      const rect = slider.getBoundingClientRect();
      const x = e.clientX - rect.left;
      handleVolumeChange(Math.max(0, Math.min(1, x / rect.width)));
    };
    const handleMouseUp = () => setIsDraggingVolume(false);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingVolume, handleVolumeChange]);

  const handleAddTime = useCallback(
    (secondsToAdd: number) => {
      if (isRunning) return;
      const newRemainingSeconds = remainingSeconds + secondsToAdd;
      setRemainingSeconds(newRemainingSeconds);
      setTotalSeconds(newRemainingSeconds);
      const newMinutes = Math.floor(newRemainingSeconds / 60);
      const newSeconds = newRemainingSeconds % 60;
      setMinutes(newMinutes);
      setSeconds(newSeconds);
      setEditingMinutes(String(newMinutes).padStart(2, '0'));
      setEditingSeconds(String(newSeconds).padStart(2, '0'));
      setEditingPart(null);
      setIsStopwatch(false);
    },
    [isRunning, remainingSeconds]
  );

  const circleSize = useMemo(() => {
    if (isMinimized) return 0;
    const availableWidth = modalWidth - 40;
    const availableHeight = modalHeight - 120;
    const next = Math.min(availableWidth, availableHeight * 0.75);
    return Math.max(220, next);
  }, [modalWidth, modalHeight, isMinimized]);

  const radius = useMemo(() => {
    if (circleSize === 0) return 0;
    const strokeWidth = circleSize / 17.5;
    return circleSize / 2 - strokeWidth / 2 - 2;
  }, [circleSize]);

  const circumference = useMemo(() => 2 * Math.PI * radius, [radius]);

  const textFontSize = useMemo(() => {
    if (circleSize === 0) return 4;
    return Math.max(1.5, Math.min((circleSize * 0.171) / 16, 7));
  }, [circleSize]);

  const textWidth = useMemo(() => {
    if (circleSize === 0) return 80;
    return circleSize * 0.229;
  }, [circleSize]);

  const buttonFontSize = useMemo(() => {
    if (circleSize === 0) return 12;
    return Math.max(10, Math.min(circleSize * 0.035, 18));
  }, [circleSize]);

  const buttonPadding = useMemo(() => {
    if (circleSize === 0) return 8;
    return Math.max(6, Math.min(circleSize * 0.025, 14));
  }, [circleSize]);

  const strokeDashoffset = circumference * (1 - progress);

  const quickButtonTop = useMemo(() => {
    if (circleSize === 0) return -30;
    return -Math.max(30, circleSize * 0.085) - 20;
  }, [circleSize]);

  const displayVolume = isMuted ? 0 : volume;

  const contentClassName = [
    'cp-edu-timer-content',
    isMinimized ? 'is-minimized' : '',
    isMaximized ? 'is-maximized' : '',
  ]
    .filter(Boolean)
    .join(' ');

  if (!open) return null;

  return createPortal(
    <div
      className="cp-edu-timer-overlay"
      data-tutorial-pres="timer-modal"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          if (isMinimized) return;
          onClose();
        }
      }}
      onTouchStart={(e) => {
        if (e.target === e.currentTarget) {
          if (isMinimized) return;
          onClose();
        }
      }}
    >
      <div
        ref={modalRef}
        className={contentClassName}
        style={{
          left: modalLeft,
          top: modalTop,
          width: isMinimized ? 200 : modalWidth,
          height: isMinimized ? 80 : modalHeight,
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        <div ref={headerRef} className="cp-edu-timer-header">
          <div className="cp-edu-timer-title">타이머</div>
          <div className="cp-edu-timer-controls">
            <button
              type="button"
              className="cp-edu-timer-window-btn"
              onClick={() => {
                setIsMinimized(true);
                setIsMaximized(false);
                const clamped = clampModalInViewport(
                  position.x,
                  position.y,
                  TIMER_MINI_W,
                  TIMER_MINI_H
                );
                setPosition(clamped.position);
              }}
              title="최소화"
              disabled={isMinimized}
            >
              −
            </button>
            <button
              type="button"
              className="cp-edu-timer-window-btn"
              onClick={() => {
                if (isMinimized) {
                  setIsMinimized(false);
                  const clamped = clampModalInViewport(
                    position.x,
                    position.y,
                    size.width,
                    size.height
                  );
                  setSize(clamped.size);
                  setPosition(clamped.position);
                } else if (isMaximized) {
                  setIsMaximized(false);
                  const clamped = clampModalInViewport(
                    position.x,
                    position.y,
                    size.width,
                    size.height
                  );
                  setSize(clamped.size);
                  setPosition(clamped.position);
                } else {
                  setIsMaximized(true);
                }
              }}
              title={isMinimized ? '확대' : isMaximized ? '일반 크기' : '전체 화면'}
            >
              {isMinimized || isMaximized ? (
                <svg className="cp-edu-timer-restore-icon" viewBox="0 0 16 16">
                  <rect
                    x="2"
                    y="4"
                    width="10"
                    height="10"
                    rx="1"
                    strokeWidth="1.5"
                    fill="rgba(255, 255, 255, 0.2)"
                  />
                  <rect x="4" y="2" width="10" height="10" rx="1" strokeWidth="1.5" fill="none" />
                </svg>
              ) : (
                <svg className="cp-edu-timer-maximize-icon" viewBox="0 0 16 16">
                  <rect x="2" y="2" width="12" height="12" rx="1" />
                </svg>
              )}
            </button>
            <button
              type="button"
              className="cp-edu-timer-window-btn"
              data-tutorial-pres="timer-close"
              onClick={onClose}
              title="닫기"
              aria-label="닫기"
            >
              ×
            </button>
          </div>
        </div>

        {isMinimized && (
          <div className="cp-edu-timer-mini-progress-track">
            <div
              className={`cp-edu-timer-mini-progress-bar${isUnderOneMinute ? ' is-urgent' : ''}`}
              style={{ width: `${progress * 100}%` }}
            />
          </div>
        )}

        <div className={`cp-edu-timer-body${isMinimized ? ' is-minimized' : ''}`}>
          {isMinimized ? (
            <div className="cp-edu-timer-mini-display">
              <span className="cp-edu-timer-mini-text">{formatTime(remainingSeconds)}</span>
              <div className="cp-edu-timer-mini-btn-wrap">
                <button
                  type="button"
                  className={`cp-edu-timer-mini-play-stop${isRunning ? ' is-running' : ''}`}
                  onClick={isRunning ? handlePause : handleStart}
                  title={isRunning ? '일시정지' : '시작'}
                  aria-label={isRunning ? '일시정지' : '시작'}
                >
                  {isRunning ? (
                    <svg className="cp-edu-timer-mini-stop-icon" viewBox="0 0 24 24">
                      <rect x="6" y="6" width="12" height="12" rx="1" />
                    </svg>
                  ) : (
                    <svg className="cp-edu-timer-mini-play-icon" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div
              className="cp-edu-timer-display-section"
              style={{
                gap: Math.min(6, modalHeight * 0.015),
                padding: `${modalHeight * 0.12}px ${Math.min(20, modalHeight * 0.04)}px ${modalHeight * 0.04}px`,
              }}
            >
              <div
                className="cp-edu-timer-circle-container"
                style={{
                  width: circleSize,
                  height: circleSize,
                  minWidth: circleSize,
                  minHeight: circleSize,
                  maxWidth: circleSize,
                  maxHeight: circleSize,
                }}
              >
                <div
                  className="cp-edu-timer-quick-btns"
                  style={{
                    top: quickButtonTop,
                    gap: Math.min(8, Math.abs(quickButtonTop) * 0.1),
                  }}
                >
                  <button
                    type="button"
                    className="cp-edu-timer-quick-btn"
                    onClick={() => handleAddTime(300)}
                    disabled={isRunning}
                    title="5분 추가"
                    style={{
                      fontSize: buttonFontSize,
                      padding: `${buttonPadding}px ${buttonPadding * 2}px`,
                    }}
                  >
                    5분
                  </button>
                  <button
                    type="button"
                    className="cp-edu-timer-quick-btn"
                    onClick={() => handleAddTime(60)}
                    disabled={isRunning}
                    title="1분 추가"
                    style={{
                      fontSize: buttonFontSize,
                      padding: `${buttonPadding}px ${buttonPadding * 2}px`,
                    }}
                  >
                    1분
                  </button>
                  <button
                    type="button"
                    className="cp-edu-timer-quick-btn"
                    onClick={() => handleAddTime(10)}
                    disabled={isRunning}
                    title="10초 추가"
                    style={{
                      fontSize: buttonFontSize,
                      padding: `${buttonPadding}px ${buttonPadding * 2}px`,
                    }}
                  >
                    10초
                  </button>
                  <button
                    type="button"
                    className="cp-edu-timer-quick-btn"
                    onClick={handleReset}
                    disabled={isRunning}
                    title="리셋"
                    style={{
                      fontSize: buttonFontSize,
                      padding: `${buttonPadding}px ${buttonPadding * 2}px`,
                    }}
                  >
                    리셋
                  </button>
                </div>

                <svg
                  width={circleSize}
                  height={circleSize}
                  viewBox={`0 0 ${circleSize} ${circleSize}`}
                  preserveAspectRatio="xMidYMid meet"
                  style={{ transform: 'rotate(-90deg)' }}
                >
                  <circle
                    cx={circleSize / 2}
                    cy={circleSize / 2}
                    r={radius}
                    fill="none"
                    stroke="rgba(255, 255, 255, 0.3)"
                    strokeWidth={circleSize / 17.5}
                  />
                  {isStopwatch ? (
                    <>
                      {Array.from({ length: completedMinutes }, (_, i) => {
                        const isEven = i % 2 === 0;
                        return (
                          <circle
                            key={`completed-${i}`}
                            cx={circleSize / 2}
                            cy={circleSize / 2}
                            r={radius}
                            fill="none"
                            stroke={getStopwatchColor(isEven)}
                            strokeWidth={circleSize / 17.5}
                            strokeDasharray={circumference}
                            strokeDashoffset={0}
                            strokeLinecap="round"
                            style={{
                              transform: 'scaleY(-1)',
                              transformOrigin: 'center',
                            }}
                          />
                        );
                      })}
                      {remainingSeconds % 60 !== 0 && (
                        <circle
                          cx={circleSize / 2}
                          cy={circleSize / 2}
                          r={radius}
                          fill="none"
                          stroke={getStopwatchColor(isCurrentMinuteEven)}
                          strokeWidth={circleSize / 17.5}
                          strokeDasharray={circumference}
                          strokeDashoffset={strokeDashoffset}
                          strokeLinecap="round"
                          style={{ transition: 'stroke-dashoffset 0.3s ease' }}
                        />
                      )}
                    </>
                  ) : (
                    <circle
                      cx={circleSize / 2}
                      cy={circleSize / 2}
                      r={radius}
                      fill="none"
                      stroke={isUnderOneMinute ? '#ff6b6b' : 'white'}
                      strokeWidth={circleSize / 17.5}
                      strokeDasharray={circumference}
                      strokeDashoffset={strokeDashoffset}
                      strokeLinecap="round"
                      style={{
                        transition: 'stroke-dashoffset 0.3s ease, stroke 0.3s ease',
                        transform: 'scaleY(-1)',
                        transformOrigin: 'center',
                      }}
                    />
                  )}
                </svg>

                <div
                  className="cp-edu-timer-text-container"
                  style={{ minWidth: circleSize * 0.5 }}
                >
                  <div className="cp-edu-timer-text-wrapper">
                    {editingPart === 'minutes' && !isRunning ? (
                      <>
                        <input
                          type="text"
                          className="cp-edu-timer-text-input"
                          value={editingMinutes}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9]/g, '');
                            if (value.length <= 2) setEditingMinutes(value);
                          }}
                          onBlur={handleMinutesBlur}
                          onKeyDown={handleMinutesKeyDown}
                          autoFocus
                          placeholder="00"
                          style={{
                            fontSize: `${textFontSize * 1.1}rem`,
                            padding: `${textFontSize * 0.12}rem 0`,
                            width: textWidth * 0.9,
                            minWidth: textWidth * 0.9,
                            maxWidth: textWidth * 0.9,
                          }}
                        />
                        <span
                          className="cp-edu-timer-text-sep"
                          style={{ fontSize: `${textFontSize * 1.1}rem` }}
                        >
                          :
                        </span>
                        <div
                          className="cp-edu-timer-text-part"
                          onClick={handleSecondsClick}
                          style={{
                            fontSize: `${textFontSize * 1.1}rem`,
                            padding: `${textFontSize * 0.12}rem 0`,
                            width: textWidth * 0.9,
                            minWidth: textWidth * 0.9,
                            maxWidth: textWidth * 0.9,
                          }}
                        >
                          {String(Math.floor(remainingSeconds % 60)).padStart(2, '0')}
                        </div>
                      </>
                    ) : editingPart === 'seconds' && !isRunning ? (
                      <>
                        <div
                          className="cp-edu-timer-text-part"
                          onClick={handleMinutesClick}
                          style={{
                            fontSize: `${textFontSize * 1.1}rem`,
                            padding: `${textFontSize * 0.12}rem 0`,
                            width: textWidth * 0.9,
                            minWidth: textWidth * 0.9,
                            maxWidth: textWidth * 0.9,
                          }}
                        >
                          {String(Math.floor(remainingSeconds / 60)).padStart(2, '0')}
                        </div>
                        <span
                          className="cp-edu-timer-text-sep"
                          style={{ fontSize: `${textFontSize * 1.1}rem` }}
                        >
                          :
                        </span>
                        <input
                          type="text"
                          className="cp-edu-timer-text-input"
                          value={editingSeconds}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9]/g, '');
                            if (value.length <= 2) setEditingSeconds(value);
                          }}
                          onBlur={handleSecondsBlur}
                          onKeyDown={handleSecondsKeyDown}
                          autoFocus
                          placeholder="00"
                          style={{
                            fontSize: `${textFontSize * 1.1}rem`,
                            padding: `${textFontSize * 0.12}rem 0`,
                            width: textWidth * 0.9,
                            minWidth: textWidth * 0.9,
                            maxWidth: textWidth * 0.9,
                          }}
                        />
                      </>
                    ) : (
                      <>
                        <div
                          className="cp-edu-timer-text-part"
                          onClick={handleMinutesClick}
                          style={{
                            fontSize: `${textFontSize * 1.1}rem`,
                            padding: `${textFontSize * 0.12}rem 0`,
                            width: textWidth * 0.9,
                            minWidth: textWidth * 0.9,
                            maxWidth: textWidth * 0.9,
                          }}
                        >
                          {String(Math.floor(remainingSeconds / 60)).padStart(2, '0')}
                        </div>
                        <span
                          className="cp-edu-timer-text-sep"
                          style={{ fontSize: `${textFontSize * 1.1}rem` }}
                        >
                          :
                        </span>
                        <div
                          className="cp-edu-timer-text-part"
                          onClick={handleSecondsClick}
                          style={{
                            fontSize: `${textFontSize * 1.1}rem`,
                            padding: `${textFontSize * 0.12}rem 0`,
                            width: textWidth * 0.9,
                            minWidth: textWidth * 0.9,
                            maxWidth: textWidth * 0.9,
                          }}
                        >
                          {String(remainingSeconds % 60).padStart(2, '0')}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="cp-edu-timer-action-wrap">
                    {!isRunning ? (
                      <button
                        type="button"
                        className="cp-edu-timer-action-btn is-primary"
                        onClick={handleStart}
                        style={{
                          padding: `${buttonPadding}px ${buttonPadding * 2}px`,
                          fontSize: buttonFontSize,
                        }}
                      >
                        시작
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="cp-edu-timer-action-btn is-secondary"
                        onClick={handlePause}
                        style={{
                          padding: `${buttonPadding}px ${buttonPadding * 2}px`,
                          fontSize: buttonFontSize,
                        }}
                      >
                        정지
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="cp-edu-timer-volume">
                <button
                  type="button"
                  className="cp-edu-timer-volume-icon-btn"
                  onClick={handleMuteToggle}
                  title={isMuted ? '음소거 해제' : '음소거'}
                >
                  {isMuted || volume === 0 ? (
                    <svg viewBox="0 0 24 24">
                      <path d="M11 5L6 9H2v6h4l5 4V5z" />
                      <line x1="23" y1="9" x2="17" y2="15" />
                      <line x1="17" y1="9" x2="23" y2="15" />
                    </svg>
                  ) : volume < 0.5 ? (
                    <svg viewBox="0 0 24 24">
                      <path d="M11 5L6 9H2v6h4l5 4V5z" />
                      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24">
                      <path d="M11 5L6 9H2v6h4l5 4V5z" />
                      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                      <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                    </svg>
                  )}
                </button>
                <div
                  className="cp-edu-timer-volume-slider"
                  data-volume-slider
                  onClick={handleVolumeSliderClick}
                  onMouseDown={handleVolumeSliderMouseDown}
                >
                  <div
                    className="cp-edu-timer-volume-track"
                    style={{ width: `${displayVolume * 100}%` }}
                  />
                  <div
                    className="cp-edu-timer-volume-thumb"
                    data-volume-handle
                    style={{ left: `${displayVolume * 100}%` }}
                  />
                </div>
                <div className="cp-edu-timer-volume-spacer" />
              </div>
            </div>
          )}
        </div>

        {!isMinimized && !isMaximized && (
          <>
            <div
              className="cp-edu-timer-resize-handle nw"
              onMouseDown={(e) => handleResizeMouseDown(e, 'nw')}
            />
            <div
              className="cp-edu-timer-resize-handle ne"
              onMouseDown={(e) => handleResizeMouseDown(e, 'ne')}
            />
            <div
              className="cp-edu-timer-resize-handle sw"
              onMouseDown={(e) => handleResizeMouseDown(e, 'sw')}
            />
            <div
              className="cp-edu-timer-resize-handle se"
              onMouseDown={(e) => handleResizeMouseDown(e, 'se')}
            />
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default EduCoordiTimerModal;
